import CrudClient from "@/services/CrudClient/";
import Vue from "vue";
import logger from "@/utilities/logger";
import i18n from "@/i18n.js";
import api from "@/services/api";
import * as signalR from "@microsoft/signalr";

async function _playSound(sound) {
	if (sound) {
		try {
			const audio = new Audio(sound);
			await audio.play();
			navigator.vibrate(200);
		} catch (error) {
			logger.$log.debug("cannot play sound");
		}
	}
}

async function _showNotification(context, message) {
	if (!("Notification" in window)) {
		logger.$log.console.warn("Notifications not enabled");
		return;
	}

	if (Notification.permission === "granted") {
		await context.dispatch("LoadUserPublicProfile", message.AuthorId);
		const profile = context.getters.UserPublicProfiles[message.AuthorId];
		let displayText = "";

		if (!message.Attributes.MessageType) {
			displayText = message.body;
		}

		switch (message.Attributes.MessageType) {
			case "Deleted":
				displayText = i18n.t("common.chatNotifications.deleted");
				break;

			case "SendFile":
				displayText = i18n.t("common.fileReceived");
				break;
		}

		if (!displayText) {
			return;
		}

		const notification = new Notification(profile.DisplayName, {
			body: displayText,
			silent: true,
			icon: profile.ImageFilePath ? profile.ImageFilePath[200] : null
		});

		notification.onclick = async event => {
			logger.$log.debug("notification clicked");
			event.preventDefault();
			parent.focus();
			window.focus();
			event.target.close();

			if (window.location.pathname.startsWith("/Chat")) {
				await context.dispatch(
					"SetCurrentConversationUserId",
					message.AuthorId
				);
			} else {
				// TODO: using ruoter in module creates navigation problems
				// find another way to redirect
				// router.push("/Chat?UserId=" + message.AuthorId);
				context.state.chatNotificationRedirectUrl =
					"/Chat?UserId=" + message.AuthorId;
			}
		};
	}
}

function getUnreadMessageCount(conversation) {
	if (!conversation.MaxMessageIndex) {
		return 0;
	}

	return conversation.MyLastSeenMessageIndex
		? conversation.MaxMessageIndex - conversation.MyLastSeenMessageIndex
		: conversation.MaxMessageIndex + 1;
}

export default {
	state: {
		signalRConnection: null,
		combinedConversationView: [],
		UserPublicProfiles: {},
		isLoadingConversationView: false,
		isChatWindowActive: false,
		isVideoCallWindowActive: false,
		conversationUploadQueue: [],
		conversationSelectedFiles: [],
		currentConversationUserId: null,
		chatNotificationRedirectUrl: null,
		signalRPingTimer: null
	},
	mutations: {
		SET_SIGNALR_PING_TIMER(state, timer) {
			state.signalRPingTimer = timer;
		},
		CLEAR_SIGNALR_PING_TIMER(state) {
			if (state.signalRPingTimer) {
				clearInterval(state.signalRPingTimer);
				state.signalRPingTimer = null;
			}
		},

		ADD_CONVERSATION_UPLOAD_QUEUE: (
			state,
			{ conversationId, uploadStatus }
		) => {
			if (state.conversationUploadQueue[conversationId] === undefined) {
				Vue.set(state.conversationUploadQueue, conversationId, []);
			}

			state.conversationUploadQueue[conversationId].push(uploadStatus);
		},

		SET_CONVERSATION_UPLOAD_QUEUE_PROGRESS: (
			state,
			{ conversationId, uploadItemIndex, progress }
		) => {
			Vue.set(
				state.conversationUploadQueue[conversationId][uploadItemIndex],
				"progress",
				progress
			);
		},

		SET_CONVERSATION_UPLOAD_QUEUE_COMPLETE: (
			state,
			{ conversationId, uploadItemIndex, completed }
		) => {
			Vue.set(
				state.conversationUploadQueue[conversationId][uploadItemIndex],
				"completed",
				completed
			);
		},

		INIT_CONVERSATION_SELECTED_FILES: (state, conversationId) => {
			logger.$log.debug("INIT_CONVERSATION_SELECTED_FILES");

			if (state.conversationSelectedFiles[conversationId] === undefined) {
				Vue.set(state.conversationSelectedFiles, conversationId, []);
			}
		},

		ADD_CONVERSATION_SELECTED_FILES: (state, { conversationId, file }) => {
			logger.$log.debug("ADD_CONVERSATION_SELECTED_FILES");

			if (state.conversationSelectedFiles[conversationId] === undefined) {
				logger.$log.error(
					`SelectedFiles not initialized. ConversationId:${conversationId}`
				);
			}

			state.conversationSelectedFiles[conversationId].push(file);
		},

		CLEAR_CONVERSATION_SELECTED_FILES: (state, conversationId) => {
			logger.$log.debug(
				"CLEAR_CONVERSATION_SELECTED_FILES:" + conversationId
			);

			if (state.conversationSelectedFiles[conversationId] === undefined) {
				logger.$log.error(
					`SelectedFiles not initialized. ConversationId:${conversationId}`
				);
			}
			state.conversationSelectedFiles[conversationId] = [];
		},

		SET_SIGNALR_CONNECTION: (state, client) => {
			state.signalRConnection = client;
		},

		SET_COMBINED_CHAT_CONVERSATIONS: (state, conversations) => {
			state.combinedConversationView = conversations;
		},

		SET_LOADING_CONVERSATION_VIEW: (state, isLoadingConversationView) => {
			// logger.$log.debug("SET_LOADING_CONVERSATION_VIEW : " + isLoadingConversationView);
			state.isLoadingConversationView = isLoadingConversationView;
		},

		SET_CHAT_WINDOW_ACTIVE: (state, windowState) => {
			state.isChatWindowActive = windowState;
		},

		SET_VIDEO_CALL_WINDOW_ACTIVE: (state, windowState) => {
			state.isVideoCallWindowActive = windowState;
		},

		SET_CURRENT_CONVERSATION_USER_ID: (state, userId) => {
			logger.$log.debug("SET_CURRENT_CONVERSATION_USER_ID: " + userId);
			state.currentConversationUserId = userId;
		},

		RESET_SIGNALR_CONNECTION: state => {
			state.signalRConnection = null;
			state.combinedConversationView = [];
			state.UserPublicProfiles = {};
			state.isLoadingConversationView = false;
			state.isChatWindowActive = false;
			state.conversationUploadQueue = [];
			state.currentConversationUserId = null;
			state.chatNotificationRedirectUrl = null;
		}
	},

	actions: {
		startSignalRPingTimer({ commit, state }) {
			// Clear any existing timer first
			commit("CLEAR_SIGNALR_PING_TIMER");

			// Create new timer that pings every 50 seconds
			const timer = setInterval(async () => {
				try {
					if (
						state.signalRConnection &&
						state.signalRConnection.state ===
							signalR.HubConnectionState.Connected
					) {
						logger.$log.debug("Sending SignalR ping");
						await state.signalRConnection.invoke("Ping");
					}
				} catch (error) {
					logger.$log.error("SignalR ping error:", error);
				}
			}, 30000);

			commit("SET_SIGNALR_PING_TIMER", timer);
		},

		AddConversationUploadQueue(context, { conversationId, uploadStatus }) {
			context.commit("ADD_CONVERSATION_UPLOAD_QUEUE", {
				conversationId,
				uploadStatus
			});
		},

		SetConversationUploadQueueProgress(
			context,
			{ conversationId, uploadItemIndex, progress }
		) {
			context.commit("SET_CONVERSATION_UPLOAD_QUEUE_PROGRESS", {
				conversationId,
				uploadItemIndex,
				progress
			});
		},

		SetConversationUploadQueueComplete(
			context,
			{ conversationId, uploadItemIndex, completed }
		) {
			context.commit("SET_CONVERSATION_UPLOAD_QUEUE_COMPLETE", {
				conversationId,
				uploadItemIndex,
				completed
			});
		},

		InitConversationSelectedFiles(context, conversationId) {
			context.commit("INIT_CONVERSATION_SELECTED_FILES", conversationId);
		},

		AddConversationSelectedFiles(context, { conversationId, file }) {
			context.commit("ADD_CONVERSATION_SELECTED_FILES", {
				conversationId,
				file
			});
		},

		ClearConversationSelectedFiles(context, conversationId) {
			context.commit("CLEAR_CONVERSATION_SELECTED_FILES", conversationId);
		},

		SetChatWindowState(context, windowState) {
			context.commit("SET_CHAT_WINDOW_ACTIVE", windowState);
		},

		SetVideoCallWindowState(context, windowState) {
			context.commit("SET_VIDEO_CALL_WINDOW_ACTIVE", windowState);
		},

		async SetCurrentConversationUserId(context, userId) {
			logger.$log.debug("SetCurrentConversationUserId: " + userId);

			if (
				context.getters.currentConversationUserId?.toLowerCase() ===
				userId.toLowerCase()
			) {
				return;
			}

			context.commit("SET_CURRENT_CONVERSATION_USER_ID", userId);

			// check if it has the twilio object, otherwise create the conversation and add to the
			const combinedConversationView = context.getters.CombinedConversationView.find(
				c => c.UserId.toLowerCase() === userId.toLowerCase()
			);

			if (
				combinedConversationView &&
				!combinedConversationView.ConversationSid
			) {
				Vue.set(
					combinedConversationView,
					"CreatingTwilioConversation",
					true
				);
				const conversationId = await context.dispatch(
					"createConversation",
					userId
				);

				logger.$log.debug("createConversation completed:" + conversationId);

				Vue.set(
					combinedConversationView,
					"ConversationSid",
					conversationId
				);

				await context.dispatch(
					"InitConversationSelectedFiles",
					conversationId
				);

				Vue.set(
					combinedConversationView,
					"CreatingTwilioConversation",
					false
				);
			}
		},

		ResetCurrentConversationUserId(context) {
			context.commit("SET_CURRENT_CONVERSATION_USER_ID", null);
		},

		async CreateSignalRConnection(context) {
			logger.$log.info("CreateSignalRConnection");
			// if no user, do not create
			if (!context.getters.isUserAuthenticated) {
				logger.$log.debug(
					"user is not signed in, chat client is not created"
				);
				return;
			}

			// TODO: chech if signalRConnection exists for the current user
			if (context.getters.SignalRConnection) {
				logger.$log.debug("user has already chat client");
				return;
			}

			try {
				const signalRConnection = new signalR.HubConnectionBuilder()
					.withUrl(api.chatSignalRUrl, {
						skipNegotiation: true,
						transport: signalR.HttpTransportType.WebSockets,
						accessTokenFactory: () => {
							return encodeURIComponent(
								context.rootState.userData.authToken
									?.accessToken
							);
						}
					})

					.withAutomaticReconnect()
					.build();

				await context.dispatch("startSignalRPingTimer");

				signalRConnection.onclose(error => {
					console.log("Connection closed:", error);
					// Your onclose handling code
				});

				signalRConnection.onreconnecting(async () => {
					logger.$log.debug("signalRConnection reconnecting");
				});
				signalRConnection.onreconnected(async () => {
					logger.$log.debug("signalRConnection reconnected");
					logger.$log.debug(
						"LoadConversations from chat reconnected"
					);
					await context.dispatch("LoadConversations");
				});

				await signalRConnection.start();
				logger.$log.debug(
					"SignalR connection started: " + signalRConnection.state
				);

				context.commit("SET_SIGNALR_CONNECTION", signalRConnection);

				logger.$log.debug("LoadConversations from create chat dialogs");
				await context.dispatch("LoadConversations");

				logger.$log.debug("Setting event handlers");

				// chat events
				signalRConnection.on("MessageAdded", async message => {
					try {
						logger.$log.debug("Chat client - message added ");
						logger.$log.debug(message);

						if (
							message.AuthorId.toLowerCase() !==
							context.getters.UserProfile.Id.toLowerCase()
						) {
							await _playSound("/audio/newmessage.mp3");

							// TODO: check vuex -> chat window open
							// show browser notification
							await _showNotification(context, message);
						}
						// update last activity time of the conversation and reorder
						const conversation = context.getters.CombinedConversationView.find(
							c => c.ConversationSid === message.ConversationSid
						);

						if (conversation) {
							conversation.MaxMessageIndex = message.MessageIndex;
							conversation.LastMessageDate = 	message.DateCreated;
							conversation.newMessageCount = getUnreadMessageCount(
								conversation
							);
						}

						await context.dispatch("SortConversationsByDate");
					} catch (err) {
						logger.$log.error(err);
					}
				});

				signalRConnection.on(
					"UpdateMyLastSeenMessageIndex",
					(conversationId, messageIndex) => {
						logger.$log.info(
							"UpdateMyLastSeenMessageIndex: " +
								conversationId +
								" " +
								messageIndex
						);
						const conversation = context.getters.CombinedConversationView.find(
							c => c.ConversationSid === conversationId
						);

						conversation.MyLastSeenMessageIndex = messageIndex;
						conversation.newMessageCount = getUnreadMessageCount(
							conversation
						);
					}
				);

				signalRConnection.on(
					"MessageSeen",
					(conversationId, messageIndex) => {
						logger.$log.info(
							"MessageSeen: " +
								conversationId +
								" " +
								messageIndex
						);
					}
				);

				signalRConnection.on("UserDisconnected", userId => {
					logger.$log.debug("UserDisconnected: " + userId);

					const combinedConversationView = context.getters.CombinedConversationView.find(
						c => c.UserId === userId
					);
					if (combinedConversationView) {
						combinedConversationView.isOnline = false;
					}
				});

				signalRConnection.on("UserConnected", userId => {
					logger.$log.debug("UserConnected: " + userId);

					const combinedConversationView = context.getters.CombinedConversationView.find(
						c => c.UserId === userId
					);
					if (combinedConversationView) {
						combinedConversationView.isOnline = true;
					}
				});

				signalRConnection.on("ConversationAdded", async () => {
					await context.dispatch("LoadConversations");
				});

				signalRConnection.on(
					"ConversationRemoved",
					async conversationId => {
						// await context.dispatch("LoadConversations");

						const index = context.getters.CombinedConversationView.findIndex(
							v => v.ConversationSid === conversationId
						);

						// Remove the item if found
						if (index !== -1) {
							const removedConversationUserId =
								context.getters.CombinedConversationView[index]
									.UserId;
							if (
								context.getters.currentConversationUserId?.toLowerCase() ===
								removedConversationUserId.toLowerCase()
							) {
								context.commit(
									"SET_CURRENT_CONVERSATION_USER_ID",
									null
								);
								await context.dispatch(
									"ClearConversationSelectedFiles",
									conversationId
								);
							}
							context.getters.CombinedConversationView.splice(
								index,
								1
							);
						}
					}
				);

				signalRConnection.on(
					"OnConversationArchived",
					async (conversationId, archived) => {
						const conversation = context.getters.CombinedConversationView.find(
							v => v.ConversationSid === conversationId
						);

						if (conversation) {
							conversation.IsArchived = archived;
						}
					}
				);
			} catch (error) {
				logger.$log.error("CreateSignalRConnection error");
				logger.$log.error(error);
			}
		},

		async LoadConversations(context) {
			logger.$log.info("Load Conversations called");

			if (!context.getters.SignalRConnection) {
				context.commit("SET_COMBINED_CHAT_CONVERSATIONS", null);
				return;
			}

			context.commit("SET_LOADING_CONVERSATION_VIEW", true);

			const conversationsListView = await context.dispatch(
				"fetchUserConversations"
			);
			// logger.$log.debug(conversationsListView);

			const onlineUsers = await context.state.signalRConnection.invoke(
				"OnlineUsers"
			);
			// logger.$log.debug("Online users: ");
			// logger.$log.debug(onlineUsers);

			for (const conversation of conversationsListView) {
				if (conversation.ConversationSid) {
					conversation.isOnline =
						onlineUsers.find(
							u =>
								u.ConversationId ===
								conversation.ConversationSid
						) !== undefined;
				}

				conversation.newMessageCount = getUnreadMessageCount(
					conversation
				);
			}

			context.commit(
				"SET_COMBINED_CHAT_CONVERSATIONS",
				conversationsListView
			);

			context.commit("SET_LOADING_CONVERSATION_VIEW", false);
		},

		SortConversationsByDate(context) {
			if (context.getters.CombinedConversationView) {
				context.getters.CombinedConversationView.sort((a, b) => {
					return a.LastMessageDate < b.LastMessageDate ? 1 : -1;
				});

				logger.$log.debug("Sorted Conversations by date");
				logger.$log.debug(
					context.getters.CombinedConversationView.map(
						c => c.LastMessageDate
					)
				);
			}
		},

		async ShutdownSignalRConnection(context) {
			if (context.state.signalRConnection) {
				logger.$log.info("shutting down chat client");

				context.state.signalRConnection.stop();
				context.commit("RESET_SIGNALR_CONNECTION");
				context.commit("CLEAR_SIGNALR_PING_TIMER");
			}
		},

		async fetchUserConversations(context) {
			try {
				const chatService = new CrudClient("Chat");

				if (context.getters.isUserInRole("Patient")) {
					return await chatService.Get(null, "PatientConversations");
				}

				if (context.getters.isUserInRole("Nutritionist")) {
					return await chatService.Get(
						null,
						"NutritionistConversations"
					);
				}
			} catch (err) {
				logger.$log.error(err);
			}
		},
		async createConversation(context, toUserId) {
			const chatService = new CrudClient("Chat");
			const createdConversation = await chatService.GetCustom(
				`DirectConversation/${toUserId}`
			);
			return createdConversation.ConversationId;
		}
	},

	getters: {
		CombinedConversationView: state => {
			return state.combinedConversationView;
		},

		SignalRConnection: state => {
			return state.signalRConnection;
		},

		TotalNewMessages: state => {
			if (!state.combinedConversationView) {
				return 0;
			}

			return state.combinedConversationView

				.map(c => getUnreadMessageCount(c))
				.reduce((prev, next) => prev + next, 0);
		},

		isLoadingConversationView: state => {
			return state.isLoadingConversationView;
		},

		isChatWindowActive: state => {
			return state.isChatWindowActive;
		},

		isVideoCallWindowActive: state => {
			return state.isVideoCallWindowActive;
		},

		currentConversationUserId: state => {
			return state.currentConversationUserId;
		},

		conversationUploadQueue: state => {
			return state.conversationUploadQueue;
		},

		chatNotificationRedirectUrl: state => {
			return state.chatNotificationRedirectUrl;
		},

		conversationSelectedFiles: state => {
			return state.conversationSelectedFiles;
		}
	}
};
